import React, { Component } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';
import { get } from 'lodash';
import {
  Col, Container, Row,
} from 'reactstrap';
import Input from 'components/forms/Input';
import Table from 'components/tables';
import TableHead from 'components/tables/tableHead';
import TableBody from 'components/tables/tableBody';
import TableCell from 'components/tables/tableCell';
import TableRow from 'components/tables/tableRow';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import debounce from 'lodash/debounce';
import LoadingIcon from 'mdi-react/LoadingIcon';
import Switch from 'components/forms/Switch';
import ButtonMenu from 'components/buttons/ButtonMenu';
import MenuItem from 'components/Menu/MenuItem';
import { STATUS_FILTER_PARAMS, NULL_STATUS } from 'constants/misc';

import { roundDown } from 'utils/misc';
import NotificationSystem from 'rc-notification';
import Pagination from 'components/pagination/Pagination';
import DestroyModal from 'components/modals/DestroyModal';
import PageTitle from 'components/titles/PageTitle';
import { toggleDestroyModal } from 'redux/actions/destroyModalActions';
import { getAccounts, updateAccount, destroyAccount } from 'redux/actions/accountActions';
import { accountDetailUrl } from 'constants/routing';
import AccountCreate from './AccountCreate';


class AccountList extends Component {
  static propTypes = {
    getAccounts: PropTypes.func.isRequired,
    toggleDestroyModal: PropTypes.func.isRequired,
    destroyAccount: PropTypes.func.isRequired,
    updateAccount: PropTypes.func.isRequired,
    accounts: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      count: PropTypes.number,
      next: PropTypes.string,
      previous: PropTypes.string,
      results: PropTypes.array,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      filterParams: {
        search: null,
        status: null,
      },
    };
    this.emitChangeDebounced = debounce(this.emitChange, 400);
  }

  componentDidMount() {
    // loading accounts
    this.fetchList();
    // eslint-disable-next-line no-return-assign
    NotificationSystem.newInstance({ style: { zIndex: '9999' } }, n => this.notification = n);
  }

  componentWillUnmount() {
    this.emitChangeDebounced.cancel();
    this.notification.destroy();
  }

  onSuccessAccountDestroyed = () => {
    this.props.toggleDestroyModal();
    this.fetchList();
  }

  handleStatusChange = account => async (e) => {
    await this.props.updateAccount(account.id, { status: e.target.checked }, true);
  }

  prepareDestroy = account => () => {
    this.props.toggleDestroyModal(account.id, account.name);
  };

  fetchList = (filterParams) => {
    this.props.getAccounts({
      ...this.state.filterParams,
      ...filterParams, // this will override params from state
    });
    this.setState({ filterParams: filterParams || this.state.filterParams });
  }

  emitChange(value) {
    const { filterParams } = this.state;
    this.fetchList({ search: value, status: filterParams.status });
  }

  render() {
    const { filterParams } = this.state;
    return (
      <Container>
        <PageTitle>Accounts</PageTitle>
        <div>
          <DestroyModal
            funcToExecute={this.props.destroyAccount}
            onSuccess={this.onSuccessAccountDestroyed}
          />
        </div>
        <Row>
          <Col className="account-list mb-super">
            <div className="d-flex flex-row flex-wrap">
              <ButtonMenu
                className="mr-1"
                btnText={get(STATUS_FILTER_PARAMS, filterParams.status, NULL_STATUS)}
                size="large"
              >
                <MenuItem onClick={() => this.fetchList({ search: filterParams.search, status: null })}>
                  All
                </MenuItem>
                <MenuItem onClick={() => this.fetchList({ search: filterParams.search, status: true })}>
                  Active
                </MenuItem>
                <MenuItem onClick={() => this.fetchList({ search: filterParams.search, status: false })}>
                  Not active
                </MenuItem>
              </ButtonMenu>
              <div className="ml-auto mr-2 search-input-form">
                <Input
                  inputProps={{ type: 'search' }}
                  rightIcon="search"
                  onChange={e => this.emitChangeDebounced(e.target.value)}
                  placeholder="Search"
                />
              </div>
              <AccountCreate />
            </div>
            {(!this.props.accounts.loading && this.props.accounts.results.length >= 1) &&
              <div className="w-100 mt-4 overflow-auto">
                <Table className="volt-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Primary email</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Total campaigns</TableCell>
                      <TableCell>Open rate</TableCell>
                      <TableCell>Reply</TableCell>
                      <TableCell>Delivered</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!this.props.accounts.loading &&
                    this.props.accounts.results.map(account => (
                      <TableRow key={account.id}>
                        <TableCell>
                          <Link to={accountDetailUrl(account.id)}>
                            {account.name}
                          </Link>
                        </TableCell>
                        <TableCell>{account.email || '-'}</TableCell>
                        <TableCell>
                          {/* eslint-disable jsx-a11y/label-has-for */}
                          <label>
                            <Switch
                              onChange={this.handleStatusChange(account)}
                              checked={account.status}
                            />
                          </label>
                        </TableCell>
                        <TableCell>{account.total_campaigns}</TableCell>
                        <TableCell>
                          {roundDown((account.stat_unique_opens / account.stat_unique_delivered) * 100)}%
                            ({account.stat_unique_opens})
                        </TableCell>
                        <TableCell>
                          {roundDown((account.stat_unique_replies / account.stat_unique_delivered) * 100)}%
                            ({account.stat_unique_replies})
                        </TableCell>
                        <TableCell>{account.stat_delivered}</TableCell>
                        <TableCell>
                          <DeleteOutlineIcon onClick={this.prepareDestroy(account)} />
                          <Link to={accountDetailUrl(account.id)}>
                            <DotsHorizontalIcon />
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                    }
                  </TableBody>
                </Table>
              </div>
            }
            {(!this.props.accounts.loading && !this.props.accounts.results.length >= 1) &&
              <h5 className="text-center mt-3">Found no accounts</h5>
            }
            {this.props.accounts.loading ? (
              <div className="loader"><LoadingIcon /></div>
            ) : (
              <Pagination
                items={this.props.accounts}
                onChangePage={this.props.getAccounts}
                params={filterParams}
              />
            )}
          </Col>
        </Row>
      </Container>
    );
  }
}

export default compose(
  connect(
    state => ({
      accounts: state.accounts,
    }),
    {
      getAccounts, toggleDestroyModal, destroyAccount, updateAccount,
    },
  ),
  withNamespaces('common'),
)(AccountList);
